import { z } from "zod";
import { attachmentDtoSchema } from "./message.js";
import { feedbackDtoSchema, orderDtoSchema } from "./order.js";
import { messageAuthorDtoSchema } from "./collaboration.js";

/**
 * The visitor context pane (PLAN.md "Client panel"). Everything here is
 * derived server-side; nothing is trusted from the widget, and there is no
 * field anywhere in this file that could hold an IP address.
 */

export const visitorDeviceSchema = z.object({
  type: z.enum(["desktop", "mobile", "tablet", "bot", "unknown"]),
  os: z.string().optional(),
  browser: z.string().optional(),
});
export type VisitorDeviceDto = z.infer<typeof visitorDeviceSchema>;

export const visitorFactKindSchema = z.enum(["name", "contact_email", "contact_phone", "interest", "constraint", "intent"]);
export type VisitorFactKindDto = z.infer<typeof visitorFactKindSchema>;

/**
 * Facts carry provenance to the message that produced them. The pane shows
 * them as *what the visitor said*, never as verified data — a wrong contact
 * detail is worse than a missing one (PLAN.md "The AI visitor profile is a
 * confident-sounding guess").
 */
export const visitorFactDtoSchema = z.object({
  id: z.string().uuid(),
  kind: visitorFactKindSchema,
  value: z.string(),
  firstSeenMessageId: z.string().uuid().nullable(),
  confidence: z.number().nullable(),
  createdAt: z.string(),
});
export type VisitorFactDto = z.infer<typeof visitorFactDtoSchema>;

/** `generatedAt` is on the wire deliberately: a confident summary of unknown age is worse than no summary. */
export const visitorProfileDtoSchema = z.object({
  summary: z.string(),
  persona: z.string().nullable(),
  interests: z.array(z.string()),
  generatedAt: z.string(),
});
export type VisitorProfileDto = z.infer<typeof visitorProfileDtoSchema>;

export const pageViewDtoSchema = z.object({
  id: z.string().uuid(),
  url: z.string(),
  path: z.string(),
  title: z.string().nullable(),
  referrer: z.string().nullable(),
  isSpaNav: z.boolean(),
  enteredAt: z.string(),
  leftAt: z.string().nullable(),
});
export type PageViewDto = z.infer<typeof pageViewDtoSchema>;

export const visitorDtoSchema = z.object({
  id: z.string().uuid(),
  device: visitorDeviceSchema.nullable(),
  language: z.string().nullable(),
  firstReferrer: z.string().nullable(),
  firstSeenAt: z.string(),
  lastSeenAt: z.string(),
  conversationsCount: z.number().int(),
  /** Resolved from the newest message that carries one, so a mid-session move is visible instead of hidden. Never a stored column on the visitor. */
  currentCountry: z.string().nullable(),
});
export type VisitorDto = z.infer<typeof visitorDtoSchema>;

// --- Panel conversation views -------------------------------------------

export const conversationListItemDtoSchema = z.object({
  id: z.string().uuid(),
  botName: z.string(),
  visitorId: z.string().uuid(),
  status: z.enum(["active", "escalated", "closed"]),
  control: z.enum(["bot", "human"]),
  lastMessagePreview: z.string().nullable(),
  lastMessageAt: z.string(),
  startedAt: z.string(),
  /** Who is handling it, if anyone. A uuid, not a name: the panel resolves it against the team it already holds, so a live `inbox.upsert` (which carries the same id) needs no second lookup. */
  assignedUserId: z.string().uuid().nullable().default(null),
  /**
   * The name the visitor gave, if they gave one — so a row reads "Maria"
   * rather than the bot's name repeated down the whole inbox. Said by the
   * visitor, never verified, exactly like the facts it comes from.
   */
  visitorName: z.string().nullable().default(null),
  /** Where they are now, from the newest message that carries a country. */
  country: z.string().nullable().default(null),
});
export type ConversationListItemDto = z.infer<typeof conversationListItemDtoSchema>;

/** One tool the bot ran mid-turn, as the transcript shows it: what it was asked, what it answered, what it cost. */
export const panelToolCallDtoSchema = z.object({
  name: z.string(),
  /** What the model passed in — the query that produced these results. */
  arguments: z.unknown(),
});
export type PanelToolCallDto = z.infer<typeof panelToolCallDtoSchema>;

/**
 * The transcript's own message shape — richer than the widget's
 * `MessageDto` (page and country per message), and it includes `role:
 * "tool"` rows, which the widget never sees.
 *
 * Tool rows are the answer to "why did the bot say that": a reply that
 * looks wrong is usually a search that returned the wrong thing, and
 * without the call in the transcript that difference is invisible.
 * `content` on such a row is the tool's JSON result.
 */
export const panelMessageDtoSchema = z.object({
  id: z.string().uuid(),
  seq: z.number().int(),
  role: z.enum(["user", "assistant", "agent", "system", "tool"]),
  content: z.string().nullable(),
  /** Present on `tool` rows (the call being answered) and on assistant rows that made one. */
  toolCalls: z.array(panelToolCallDtoSchema).nullable(),
  /** How long the tool took, or how long the turn took. */
  latencyMs: z.number().nullable(),
  /** Decimal string; set only where something was billable (e.g. `search_knowledge`'s embedding call). */
  costUsd: z.string().nullable(),
  attachments: z.array(attachmentDtoSchema).default([]),
  pageUrl: z.string().nullable(),
  pageTitle: z.string().nullable(),
  country: z.string().nullable(),
  /**
   * The teammate who wrote an agent message, or who handed the conversation
   * back. Null for visitor, bot and tool rows — and for an agent row whose
   * author has since been deleted, which still reads as "Agent".
   */
  author: messageAuthorDtoSchema.nullable().default(null),
  createdAt: z.string(),
});
export type PanelMessageDto = z.infer<typeof panelMessageDtoSchema>;

export const conversationDetailDtoSchema = z.object({
  id: z.string().uuid(),
  botName: z.string(),
  status: z.enum(["active", "escalated", "closed"]),
  control: z.enum(["bot", "human"]),
  assignedUserId: z.string().uuid().nullable().default(null),
  entryUrl: z.string().nullable(),
  startedAt: z.string(),
  lastMessageAt: z.string(),
  closedAt: z.string().nullable(),
  messages: z.array(panelMessageDtoSchema),
  visitor: visitorDtoSchema,
  profile: visitorProfileDtoSchema.nullable(),
  facts: z.array(visitorFactDtoSchema),
  pageJourney: z.array(pageViewDtoSchema),
  /** Drafts included — "they built a basket and didn't press Confirm" is a thing sales wants to see. */
  orders: z.array(orderDtoSchema),
  feedback: z.array(feedbackDtoSchema),
});
export type ConversationDetailDto = z.infer<typeof conversationDetailDtoSchema>;

export const visitorDetailDtoSchema = z.object({
  visitor: visitorDtoSchema,
  profile: visitorProfileDtoSchema.nullable(),
  facts: z.array(visitorFactDtoSchema),
  conversations: z.array(conversationListItemDtoSchema),
  recentPages: z.array(pageViewDtoSchema),
});
export type VisitorDetailDto = z.infer<typeof visitorDetailDtoSchema>;
